/**
 * TURN CC · 28 — Scheduler status hook.
 *
 * Fetches `/api/scheduler/status` and exposes the raw job list. Aligned on
 * the platform-wide instant-hydration pattern (persister + gcTime 24h),
 * with a 60s refetch interval so the "Next Automated Brief" countdown
 * stays accurate as we approach the cron fire time.
 */
import { useQuery } from "@tanstack/react-query";
import { API, authHeaders, apiJson } from "@/lib/api";

export const SCHEDULER_STATUS_KEY = ["scheduler", "status"];

export function schedulerStatusQueryFn() {
  return apiJson(`${API}/scheduler/status`, { headers: authHeaders() });
}

export function useSchedulerStatus() {
  return useQuery({
    queryKey: SCHEDULER_STATUS_KEY,
    queryFn: schedulerStatusQueryFn,
    staleTime: 60_000,                 // 1 min fresh
    gcTime: 24 * 60 * 60_000,          // 24h — matches persister maxAge
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60_000,           // silent revalidation once per minute
  });
}

/**
 * Pick a specific job by id from the scheduler status payload. Returns
 * `null` if the payload is missing or the job is not registered.
 */
export function pickJob(status, jobId) {
  const jobs = status?.jobs;
  if (!Array.isArray(jobs)) return null;
  return jobs.find((j) => j?.id === jobId) || null;
}
